import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEOHead from "./SEOHead";
import logo from "../assets/logo.jpg";
import "bootstrap/dist/css/bootstrap.min.css";
import "./DeleteAccount.css";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.45 } }),
};

const DATA_TABLE = [
  { type: "Profile (name, email, photo, bio)",  action: "deleted", retention: "Within 30 days" },
  { type: "Posts, comments, listings",           action: "deleted", retention: "Within 30 days" },
  { type: "Direct messages",                     action: "deleted", retention: "Within 30 days" },
  { type: "Saved items and preferences",         action: "deleted", retention: "Within 30 days" },
  { type: "Transaction / legal records (if any)",action: "kept",    retention: "As required by law (up to 7 years)" },
  { type: "Anonymised analytics data",           action: "kept",    retention: "Retained in anonymised form" },
];

const Step = ({ num, title, body }) => (
  <li className="da-step">
    <div className="da-step-num">{num}</div>
    <div className="da-step-text">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  </li>
);

const DeleteAccount = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        title="Delete Your Account – ComLinkr"
        description="Learn how to permanently delete your ComLinkr account and what happens to your data."
        canonical="https://www.comlinkr.com/delete-account"
      />

      {/* Header */}
      <div className="da-header">
        <img
          src={logo}
          alt="ComLinkr logo"
          className="da-header-logo"
          onClick={() => navigate("/")}
        />
        <motion.h1
          className="da-header-title"
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Delete Your Account
        </motion.h1>
        <motion.p
          className="da-header-sub"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          We're sorry to see you go. Here's everything you need to know about deleting your ComLinkr account.
        </motion.p>
      </div>

      <div className="da-container">

        {/* Warning box */}
        <motion.div
          className="da-warning"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <p className="da-warning-lead">
            ⚠️ Account deletion is permanent and cannot be undone. Before proceeding, please note:
          </p>
          <ul className="da-warning-list">
            <li>All your posts, listings, and community content will be permanently deleted</li>
            <li>Your profile, messages, and account data will be removed</li>
            <li>Any active property agreements or applications will be cancelled</li>
            <li>You will lose access to all your saved items and history</li>
          </ul>
        </motion.div>

        {/* Method 1 */}
        <motion.section
          className="da-section"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <h2 className="da-section-title">Method 1 — Delete from the App (Recommended)</h2>
          <ul className="da-steps">
            <Step num={1} title="Open ComLinkr"
              body="Launch the ComLinkr app on your Android or iOS device and make sure you are logged in." />
            <Step num={2} title="Go to Settings"
              body="Tap the hamburger menu (☰) in the top-left corner, then tap Profile → Settings." />
            <Step num={3} title='Find "Delete Account"'
              body='Scroll to the bottom of the Settings screen. You will see a red "Delete account" button.' />
            <Step num={4} title="Confirm Deletion"
              body='Tap Delete account → tap Continue on the confirmation prompt. Your account will be scheduled for deletion and you will be signed out immediately.' />
            <Step num={5} title="Deletion Complete"
              body="Your account and all associated data will be permanently deleted within 30 days of your request." />
          </ul>
        </motion.section>

        <hr className="da-hr" />

        {/* Method 2 */}
        <motion.section
          className="da-section"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          <h2 className="da-section-title">Method 2 — Request via Email</h2>
          <p className="da-section-intro">
            If you cannot access the app, you can request account deletion by emailing us directly.
          </p>
          <ul className="da-steps">
            <Step num={1} title="Send an email to support@comlinkr.com"
              body='Use the subject line: "Delete My Account Request"' />
            <Step num={2} title="Include your account details"
              body="Include your registered email address and full name so we can locate your account." />
            <Step num={3} title="We will confirm within 48 hours"
              body="Our support team will confirm your deletion request by email within 48 hours." />
            <Step num={4} title="Account deleted within 30 days"
              body="Your account and all data will be permanently deleted within 30 days of confirmation." />
          </ul>
        </motion.section>

        <hr className="da-hr" />

        {/* Data table */}
        <motion.section
          className="da-section"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={2}
        >
          <h2 className="da-section-title">What Data Gets Deleted vs Kept</h2>
          <div className="da-table-wrap">
            <table className="da-table">
              <thead>
                <tr>
                  <th>Data Type</th>
                  <th>Action</th>
                  <th>Retention Period</th>
                </tr>
              </thead>
              <tbody>
                {DATA_TABLE.map(({ type, action, retention }) => (
                  <tr key={type}>
                    <td>{type}</td>
                    <td>
                      <span className={action === "deleted" ? "da-badge da-badge--deleted" : "da-badge da-badge--kept"}>
                        {action === "deleted" ? "Deleted" : "Kept"}
                      </span>
                    </td>
                    <td>{retention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.section>

        {/* Contact */}
        <motion.div
          className="da-contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={3}
        >
          <h2 className="da-contact-title">Need help?</h2>
          <p className="da-contact-sub">Our support team is happy to assist you with your account deletion request.</p>
          <a
            href="mailto:support@comlinkr.com?subject=Delete My Account Request"
            className="da-contact-btn"
          >
            Email: support@comlinkr.com
          </a>
          <p className="da-contact-note">We respond within 48 hours · ComLinkr · www.comlinkr.com</p>
        </motion.div>

        <div className="da-footer">
          <p>© 2026 ComLinkr. All rights reserved.</p>
          <p>
            <a href="/privacy">Privacy Policy</a> · <span style={{ cursor: "pointer", color: "#0076EB" }} onClick={() => navigate("/")}>Home</span>
          </p>
        </div>

      </div>
    </>
  );
};

export default DeleteAccount;
